import {
    AmbientLight,
    ArrowHelper,
    BoxGeometry,
    BufferGeometry,
    DirectionalLight,
    EdgesGeometry,
    GridHelper,
    Group,
    Line,
    LineBasicMaterial,
    LineSegments,
    Mesh,
    MeshBasicMaterial,
    MeshLambertMaterial,
    PerspectiveCamera,
    Scene,
    Vector3,
    WebGLRenderer,
} from 'three';
import gentilisBold from 'three/examples/fonts/gentilis_bold.typeface.json';
import gentilisRegular from 'three/examples/fonts/gentilis_regular.typeface.json';
import helvetikerBold from 'three/examples/fonts/helvetiker_bold.typeface.json';
import helvetikerRegular from 'three/examples/fonts/helvetiker_regular.typeface.json';
import optimerBold from 'three/examples/fonts/optimer_bold.typeface.json';
import optimerRegular from 'three/examples/fonts/optimer_regular.typeface.json';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader';
import { colorParser, factorNumList } from './utils';

class ThreeGL {
    #fontFamilies; // 字体类型
    #fontLoader = new FontLoader(); // 字体加载器
    #directionalLight; // 平行光
    #ambientLight; // 环境光
    constructor() {
        this.scene = null; // 场景
        this.camera = null; // 相机
        this.renderer = null; // 渲染器
        this.controls = null; // 控制器
        // 预加载可用的字体类型
        this.#fontFamilies = {
            helvetikerRegular: this.#fontLoader.parse(helvetikerRegular),
            helvetikerBold: this.#fontLoader.parse(helvetikerBold),
            gentilisRegular: this.#fontLoader.parse(gentilisRegular),
            gentilisBold: this.#fontLoader.parse(gentilisBold),
            optimerRegular: this.#fontLoader.parse(optimerRegular),
            optimerBold: this.#fontLoader.parse(optimerBold),
        };
    }
    definedScene = () => {
        if (!this.scene) {
            this.scene = new Scene();
            // 添加雾化效果
            //this.scene.fog = new FogExp2(0xffffff, 2 / 1000);
        }
    };
    definedCamera = (fov, aspect, near, far) => {
        if (!this.camera) {
            this.camera = new PerspectiveCamera(fov, aspect, near, far);
        }
    };
    definedRenderer = (parameters) => {
        if (!this.renderer) {
            this.renderer = new WebGLRenderer(parameters);
        }
    };
    definedControls = (camera, domElement) => {
        if (!this.controls) {
            this.controls = new OrbitControls(camera, domElement);
        }
    };
    addDirectionalLight = (color, intensity, beforeAdd = null) => {
        if (!this.#directionalLight && this.scene) {
            this.#directionalLight = new DirectionalLight(color, intensity);
            typeof beforeAdd === 'function' && beforeAdd(this.#directionalLight);
            this.scene.add(this.#directionalLight);
        }
    };
    addAmbientLight = (color, intensity) => {
        if (!this.#ambientLight && this.scene) {
            this.#ambientLight = new AmbientLight(color, intensity);
            this.scene.add(this.#ambientLight);
        }
    };
    /**
     * 文本缓冲几何体
     * @param text {string} 将要显示的文本
     * @param parameters {object} 参数的对象
     * @returns TextGeometry 几何体
     */
    textGeometry = (text, parameters = {}) => {
        const font = Object.keys(this.#fontFamilies).includes(parameters.font)
            ? this.#fontFamilies[parameters.font]
            : this.#fontFamilies['helvetikerRegular'];
        return new TextGeometry(text, {
            ...parameters,
            font: font,
        });
    };
    createGroup = () => new Group();
    /**
     *
     */
    createGridHelper = (size, divisions, color1, beforeAdd) => {
        const { color, transparent, opacity } = colorParser(color1);
        const gridHelper = new GridHelper(size, divisions, color, color);
        gridHelper.material.transparent = transparent;
        gridHelper.material.opacity = opacity;
        gridHelper.rotation.set(Math.PI / 2, 0, 0);
        typeof beforeAdd === 'function' && beforeAdd(gridHelper);
        return gridHelper;
    };
    /**
     * 四边形几何体
     */
    createBox = ({
        name = '',
        width = 0,
        length = 0,
        height = 0,
        x = 0,
        y = 0,
        z = 0,
        borderColor,
        coverColor,
        visible,
        openMidline,
    }) => {
        const borderparameters = colorParser(borderColor);
        const coverparameters = colorParser(coverColor);
        const box = new Group();
        const geometry = new BoxGeometry(length, width, height);
        const lineMaterial = new LineBasicMaterial(borderparameters);
        const meshMaterial = new MeshLambertMaterial(coverparameters);
        const line = new LineSegments(new EdgesGeometry(geometry), lineMaterial);
        const mesh = new Mesh(geometry, meshMaterial);
        line.position.set(x + length / 2, y + width / 2, z + height / 2);
        mesh.position.set(x + length / 2, y + width / 2, z + height / 2);
        //
        switch (visible) {
            case 'border':
                mesh.visible = false;
                line.visible = true;
                break;
            case 'cover':
                mesh.visible = true;
                line.visible = false;
                break;
            default:
                mesh.visible = line.visible = !!visible;
                break;
        }
        // 画箱体上开口标线
        const long = Math.max(width, length) / 5; // 线条长度五等分
        const i = Number(width > length); // 0 | 1 ：线条方向
        const base = new Vector3(x, y, z); // 箱子本身的坐标
        const v3 = new Vector3(length / 2, width / 2, height); // 定位到上表面的中间
        const lineStart = v3.clone().setComponent(i, long).add(base);
        const lineEnd = v3
            .clone()
            .setComponent(i, long * 4)
            .add(base);
        const outLine = new Line(
            new BufferGeometry().setFromPoints([lineStart, lineEnd]),
            lineMaterial,
        );
        outLine.visible = !!openMidline;
        box.add(line, mesh, outLine);
        box.name = name;
        box.castShadow = true;
        return box;
    };
    /**
     *
     */
    createAxis = (name, parameters) => {
        const { size, length, color: _color } = parameters;
        const { color, transparent, opacity } = colorParser(_color);
        const group = new Group();
        const axis = new ArrowHelper(
            new Vector3().setComponent(name, 1),
            new Vector3(),
            length + size * 4,
            color,
            size * 2,
        );
        axis.line.material.transparent = axis.cone.material.transparent = transparent;
        axis.line.material.opacity = axis.cone.material.opacity = opacity;
        group.add(axis);
        // constant
        const scaleInterval = +(size * 4).toFixed(1); // 每一个大刻度之间的增量值
        const scaleCount = Math.ceil(length / scaleInterval);
        const [minFactor, maxFactor] = factorNumList(scaleInterval * 10); // 取中间因数作为小刻度数值
        const scaleStrings = []; // 刻度文本数组
        while (scaleStrings.length < scaleCount) {
            scaleStrings.push(+((scaleStrings.length + 1) * scaleInterval).toFixed(2));
        }
        const lineBasic = new LineBasicMaterial({ color, transparent, opacity }); // 刻度线材质
        const textBasic = new MeshBasicMaterial({ color, transparent, opacity }); // 文字材质

        for (let j = 0; j < scaleStrings.length; j++) {
            const text = scaleStrings[j]; // 大刻度上的数值
            // 循环添加大刻度中的全部刻度，倒序从大到小添加
            for (let time = 0; time < maxFactor; time++) {
                const groupVal = text - (minFactor / 10) * time; // 每次刻度的值,定位
                if (groupVal > length) {
                    continue;
                }
                const len = time ? size / 2 : size; // 刻度线是长是短
                const lineGeometry = new BufferGeometry();
                const lineStart = new Vector3().setComponent(name, groupVal);
                const lineEnd = lineStart.clone().setComponent((name + 1) % 2, -len); // 刻度只取两种方向
                lineGeometry.setFromPoints([lineStart, lineEnd]);
                group.add(new Line(lineGeometry, lineBasic));
            }
            // 大刻度数值超过，结束循环
            if (text > length) {
                break;
            }
            const geometry = this.textGeometry(String(text), {
                size,
                height: size / 10,
                curveSegments: 12,
            });
            const fontMesh = new Mesh(geometry.center(), textBasic);
            // 调整数标的位置
            fontMesh.rotateZ((Math.PI / 2) * ((name % 2) + 1));
            fontMesh.rotateX((Math.PI / 2) * (name === 2));
            fontMesh.position.setComponent(name, text).setComponent((name + 1) % 2, -size * 2);
            group.add(fontMesh);
        }
        return group;
    };
    /**
     *
     */
    createBoundary = ({ length, width, height, borderColor, coverColor }) => {
        const border = colorParser(borderColor);
        const cover = colorParser(coverColor);
        const geometry = new BoxGeometry(length, width, height);
        const lineMaterial = new LineBasicMaterial(border);
        const meshMaterial = new MeshLambertMaterial(cover);
        const line = new LineSegments(new EdgesGeometry(geometry), lineMaterial);
        const mesh = new Mesh(geometry, meshMaterial);
        line.position.set(length / 2, width / 2, height / 2);
        mesh.position.set(length / 2, width / 2, height / 2);
        return [line, mesh];
    };
    /**
     *
     */
    getCanvasElement = (x, y, z) => {
        const offset = new Vector3(x, y, z);
        if (this.scene && this.camera && this.renderer) {
            this.scene.position.add(offset);
            this.renderer.clear();
            this.renderer.render(this.scene, this.camera);
            return this.renderer.domElement;
        }
        return null;
    };
    /**
     *
     */
    clear = () => {
        this.scene.clear();
        this.renderer.clear();
    };
}

export default ThreeGL;
